import React from 'react';
import { MaterialReactTable } from 'material-react-table';
import { convertToReadableFormat } from '../../../utils/helper.jsx';
import { Tooltip, Typography } from '@mui/material';

interface EllipsisWithTooltipProps {
  text: string | number | null | undefined;
  maxWidth?: number | string;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  fontSize?: number | string;
  fontWeight?: number | string;
  color?: string;
}

export const EllipsisWithTooltip: React.FC<EllipsisWithTooltipProps> = ({
  text = '',
  maxWidth = 180,
  tooltipPlacement = 'top',
  fontSize = 12.25,
  fontWeight = 400,
  // color = 'text.primary',
}) => {
  const textRef = React.useRef < HTMLElement | null > (null);
  const [showTooltip, setShowTooltip] = React.useState(false);

  React.useEffect(() => {
    const el = textRef.current;
    if (el) setShowTooltip(el.scrollWidth > el.clientWidth);
  }, [text]);

  return (
    <Tooltip
      title={showTooltip ? text : ''}
      placement={tooltipPlacement}
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: 12,
            borderRadius: 1.5,
            px: 1,
            py: 0.5,
            bgcolor: 'grey.800',
          },
        },
      }}
    >
      <Typography
        ref={textRef}
        noWrap
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth,
          fontSize,
          fontWeight,
          // color,
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      >
        {text || '-'}
      </Typography>
    </Tooltip>
  );
};


interface TableProps {
  columns: any[];
  data: any[];
  isLoading?: boolean;
  enableRowNumbers?: boolean;
  enableDensityToggle?: boolean;
  enableTopToolbar?: boolean;
  enableFullScreenToggle?: boolean;
  enableTableFooter?: boolean;
  enableBottomToolbar?: boolean;
  enableColumnActions?: boolean;
  enableColumnFilters?: boolean;
  enableHiding?: boolean;
  enableStickyHeader?: boolean;
  renderTopToolbar?: any;
  manualSorting?: boolean;
  muiTableContainerProps?: any;
  muiTableHeadCellProps?: any;
  muiTableBodyCellProps?: any;
  initialState?: any;
  state?: any;
  renderBottomToolbar?: any;
  enablePagination?: boolean;
  manualPagination?: boolean;
  onSortingChange?: any;
  enableRowSelection?: boolean;
  onRowSelectionChange?: any;
  getRowId?: any;
  enableMultiRowSelection?: boolean;
  enableRowOrdering?: boolean;
  muiRowDragHandleProps?: any;
  renderTopToolbarCustomActions?: any;
  renderToolbarInternalActions?: any;
  manualFiltering?: boolean;
  onGlobalFilterChange?: any;
  enableRowActions?: boolean;
  renderRowActionMenuItems?: any;
  renderRowActions?: any;
  enableColumnResizing?: boolean;
  onColumnVisibilityChange?: any;
  renderBottomToolbarCustomActions?: any;
  enableRowVirtualization?: boolean;
  rowVirtualizerInstanceRef?: React.RefObject<any>;
}

// const prettifyHeader = (key: string) => {
//   if (!key) return '';
//   return key
//     .replace(/_/g, ' ')
//     .replace(/\b\w/g, char => char.toUpperCase());
// };

const CommonTable: React.FC<TableProps> = ({
  columns,
  data,
  isLoading = false,
  enableRowNumbers = false,
  enableDensityToggle = false,
  enableFullScreenToggle = false,
  enableColumnFilters = false,
  enableColumnActions = false,
  enableTableFooter = true,
  enableBottomToolbar = true,
  renderTopToolbar,
  enableTopToolbar = false,
  enableHiding = false,
  muiTableContainerProps = { sx: { maxHeight: 'calc(100vh - 362px)', width: '100%' } },
  muiTableHeadCellProps = {
    sx: {
      backgroundColor: 'primary.main',
      // backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
      color: '#ffffff',
      fontWeight: '400 !important',
      fontSize: '14px !important',
      letterSpacing: '0.3px',
      textRendering: 'geometricPrecision',
      WebkitFontSmoothing: 'none',
      MozOsxFontSmoothing: 'auto',
      // fontFamily: 'Arial, sans-serif',
      fontFamily: 'system-ui !important',
      transform: 'translateZ(0)',
       textAlign: 'center',            // Centers text inside cell
    '& .Mui-TableHeadCell-Content': {
      justifyContent: 'center',      
    },
      '& .Mui-TableHeadCheckbox-Checkbox': {
        color: '#fff !important',
      },
      '& .MuiCheckbox-root': {
        color: '#fff !important', // Ensures checkbox is white
      },
      '& .MuiSvgIcon-root': {
        color: '#fff !important', // Ensures checkbox is white
      },
      '& .MuiTableSortLabel-iconDirectionAsc': {
        color: '#fff !important',
      },
      '& .MuiTableSortLabel-iconDirectiondesc': {
        color: '#fff !important',
      },
      '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
        color: '#fff !important',
      },
      '& .MuiTableSortLabel-icon': {
        color: '#fff !important',
      },
    },
  },
  muiTableBodyCellProps = {
    sx: (theme) => ({
      backgroundColor:
        theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
          textAlign: 'center',
        justifyContent: 'center',
    }),
  },
  state = { isLoading: false, sorting: [] },
  initialState = { density: 'compact' },
  onSortingChange,
  renderBottomToolbar,
  enableRowSelection = false,
  onRowSelectionChange = null,
  getRowId,
  enableMultiRowSelection = false,
  enableRowOrdering = false,
  muiRowDragHandleProps,
  renderTopToolbarCustomActions,
  manualFiltering = false,
  onGlobalFilterChange,
  enableRowActions = false,
  renderRowActionMenuItems,
  renderToolbarInternalActions,
  renderRowActions,
  enableColumnResizing = false,
  onColumnVisibilityChange,
  renderBottomToolbarCustomActions,
  enableRowVirtualization = false,
  rowVirtualizerInstanceRef,
}) => {
  const formattedColumns = columns.map((col) => ({
    ...col,
    // header: typeof col.header === 'string' ? convertToReadableFormat(col.header) : col.header,
    Header: () => typeof col.header === 'string' ? <EllipsisWithTooltip text={convertToReadableFormat(col.header)} fontSize={13} fontWeight={450} /> : <EllipsisWithTooltip text={col.header} fontSize={13} fontWeight={450} />,
  }));

  return (
    <MaterialReactTable
      columns={columns}
      data={data}
      enableRowNumbers={enableRowNumbers}
      enableRowSelection={enableRowSelection}
      getRowId={getRowId}
      onRowSelectionChange={onRowSelectionChange}
      muiTableBodyCellProps={muiTableBodyCellProps}
      muiTableContainerProps={muiTableContainerProps}
      muiTableHeadCellProps={muiTableHeadCellProps}
      enableDensityToggle={enableDensityToggle}
      enableFullScreenToggle={enableFullScreenToggle}
      enableColumnActions={enableColumnActions}
      enableColumnFilters={enableColumnFilters}
      enableHiding={enableHiding}
      enableStickyHeader={true}
      // enableTopToolbar={false}
      enableTopToolbar={enableTopToolbar}
      enablePagination={false}
      manualPagination={false}
      enableBottomToolbar={enableBottomToolbar}
      enableTableFooter={enableTableFooter}
      initialState={initialState}
      state={state}
      onSortingChange={onSortingChange}
      renderBottomToolbar={renderBottomToolbar}
      renderTopToolbar={renderTopToolbar}
      enableMultiRowSelection={enableMultiRowSelection}
      enableRowOrdering={enableRowOrdering}
      muiRowDragHandleProps={muiRowDragHandleProps}
      renderTopToolbarCustomActions={renderTopToolbarCustomActions}
      enableGlobalFilterModes={false}
      manualFiltering={manualFiltering}
      onGlobalFilterChange={onGlobalFilterChange}
      enableRowActions={enableRowActions}
      renderRowActionMenuItems={renderRowActionMenuItems}
      renderToolbarInternalActions={renderToolbarInternalActions}
      renderRowActions={renderRowActions}
      enableColumnResizing={enableColumnResizing}
      onColumnVisibilityChange={onColumnVisibilityChange}
      renderBottomToolbarCustomActions={renderBottomToolbarCustomActions}
      enableRowVirtualization={enableRowVirtualization}
      rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}//get access to the virtualizer instance
      rowVirtualizerOptions={{ overscan:8,estimateSize: () => 100 }}

      positionToolbarAlertBanner="top"
    // defaultColumn={{
    //   maxSize: 400,
    //   minSize: 80,
    //   size: 180,
    // }}
    // muiTopToolbarProps={{
    //   sx: {
    //     '& .MuiInputBase-root, & .MuiIconButton-root': {
    //       display: 'none',
    //     },
    //   },
    // }}
    />
  );
};

export default CommonTable;
